const DB_NAME = 'video-gen-db';
const DB_VERSION = 1;

type StoreName = 'apiKeys' | 'projects' | 'vrmCache' | 'settings';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('apiKeys')) db.createObjectStore('apiKeys');
      if (!db.objectStoreNames.contains('projects'))
        db.createObjectStore('projects', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('vrmCache'))
        db.createObjectStore('vrmCache', { keyPath: 'asset_id' });
      if (!db.objectStoreNames.contains('settings'))
        db.createObjectStore('settings');
    };
  });
  return dbPromise;
}

async function tx<T = unknown>(
  store: StoreName,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T> | IDBRequest<T>[],
): Promise<T | T[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    const res = fn(s);
    t.oncomplete = () => {
      if (Array.isArray(res)) {
        resolve(res.map((r) => r.result));
      } else {
        resolve(res.result);
      }
    };
    t.onerror = () => reject(t.error);
  });
}

export const idb = {
  get: <T = unknown>(store: StoreName, key: string) =>
    tx<T>(store, 'readonly', (s) => s.get(key) as IDBRequest<T>),

  put: <T = unknown>(store: StoreName, value: T, key?: string) =>
    tx(store, 'readwrite', (s) =>
      key !== undefined ? s.put(value, key) : s.put(value),
    ),

  delete: (store: StoreName, key: string) =>
    tx(store, 'readwrite', (s) => s.delete(key)),

  keys: (store: StoreName) =>
    tx(store, 'readonly', (s) => s.getAllKeys() as IDBRequest<any>),
};
